import { Controller } from "@hotwired/stimulus"
import { swapSelectOptions, isSwappingOptions } from "../lib/slim_options"

// Universal faceted search controller.
//
// One of TWO faceted-cascade families (ADR-0059). This is the SEARCH family,
// backed by the `FacetedSearchable` concern (server-side `facet_options`), for
// search and create/edit forms where one select scopes another's options to
// resolve or capture an entity value (single value, dependent starts empty,
// the chosen value is submitted). Its sibling is `faceted_filter_controller.js`
// (with per-controller `filter_options` actions, ADR-0045), for report filters
// that narrow a result set. See ADR-0059 for the rule on which to use.
//
// Usage:
//   <div data-controller="faceted-search"
//        data-faceted-search-url-value="/images/facet_options"
//        data-faceted-search-required-value='["client_name","retailer_name"]'>
//
//     <select data-faceted-search-target="facet"
//             data-facet-key="client_name"
//             data-facet-triggers="mission_cycle_id,mission_id">
//
//     <select data-faceted-search-target="facet"
//             data-facet-key="mission_cycle_id"
//             data-facet-triggers="mission_id">
//
//     <select data-faceted-search-target="facet"
//             data-facet-key="mission_id">
//
//     <button data-faceted-search-target="submit">Go!</button>
//   </div>
//
// How it works:
//   - Each <select> is a "facet" with a unique `data-facet-key`
//   - `data-facet-triggers` lists which other facets should be refreshed when this one changes
//   - On change, the controller POSTs all current facet values to the URL
//   - The server returns `{ facet_key: [[label, value], ...], ... }` for each triggered facet
//   - The controller updates the triggered selects in place (Slim Select compatible)
//   - Submit button is disabled until all required facets have a value
//
// "Refresh all" mode (ADR-0059 amendment, ADR-0129) -- opt in with
// data-faceted-search-mode-value="all". There is no parent-to-child chain:
// every facet narrows every other one, in any order.
//
//   <div data-controller="faceted-search"
//        data-faceted-search-url-value="/images/facet_options"
//        data-faceted-search-mode-value="all">
//     <select data-faceted-search-target="facet" data-facet-key="client_name"
//             data-facet-label="Client" data-action="change->faceted-search#changed">
//     <input type="date" data-faceted-search-target="input" data-facet-key="date_from"
//            data-action="change->faceted-search#changed">
//     <span data-faceted-search-target="count"></span>
//     <span data-faceted-search-target="scopeNote" data-facet-key="tag_list" class="hidden">all</span>
//     <p data-faceted-search-target="dropped" class="hidden"></p>
//
//   - Changes are debounced (~150ms), then ONE request carries every current
//     value with `facets: "all"` and the key that changed; the server returns
//     `{ facets: { key: [[label, value], ...] }, count, count_capped, unscoped }`
//   - A newer request aborts the one in flight (AbortController)
//   - Each refreshing select shows a loading state until its options land
//   - Still-valid selections survive the option swap; selections that are no
//     longer possible are dropped and listed in the `dropped` target, then
//     the other facets refresh again off the smaller selection
//   - `input` targets (dates, radios) are sent as values and trigger a refresh
//     but never receive options
//
export default class extends Controller {
  static targets = ["facet", "input", "submit", "submitLabel", "submitSpinner", "hint", "count", "scopeNote", "dropped"]
  static values = {
    url: String,
    required: { type: Array, default: [] },
    requiredLabels: { type: Object, default: {} },
    mode: { type: String, default: "triggers" },
    debounce: { type: Number, default: 150 }
  }

  connect() {
    // Delay to let Slim Select initialise (it uses a 100ms setTimeout)
    setTimeout(() => this.updateSubmitState(), 150)

    // Show spinner when form submits
    this._boundShowSpinner = () => this.showSpinner()
    this.element.closest("form")?.addEventListener("submit", this._boundShowSpinner)

    if (this.refreshAllMode) {
      this._changedKeys = new Set()
      this._refreshEverything = false
      this._optionSignatures = new WeakMap()
      // First refresh narrows options to any selection already in the URL
      // and fills the live count. Waits for Slim like updateSubmitState.
      this._initialRefresh = setTimeout(() => this.queueRefresh(null), 150)
    }
  }

  disconnect() {
    this.element.closest("form")?.removeEventListener("submit", this._boundShowSpinner)
    clearTimeout(this._hintTimeout)
    clearTimeout(this._initialRefresh)
    clearTimeout(this._debounceTimer)
    clearTimeout(this._droppedTimeout)
    this._abortController?.abort()
  }

  get refreshAllMode() {
    return this.modeValue === "all"
  }

  showSpinner() {
    if (this.hasSubmitLabelTarget) this.submitLabelTarget.classList.add("hidden")
    if (this.hasSubmitSpinnerTarget) this.submitSpinnerTarget.classList.remove("hidden")
    if (this.hasSubmitSpinnerTarget) this.submitSpinnerTarget.classList.add("flex")
  }

  // Called by any facet select on change (wire with data-action="change->faceted-search#changed")
  async changed(event) {
    if (this.refreshAllMode) {
      // Slim's setData fires `change` when we swap options -- not a user change.
      if (isSwappingOptions(event.target)) return

      this.updateSubmitState()
      this.queueRefresh(event.target.dataset.facetKey)
      return
    }

    this.updateSubmitState()

    const triggers = (event.target.dataset.facetTriggers || "").split(",").filter(k => k)

    if (triggers.length === 0) return

    const params = this.currentValues()
    params.facets = triggers

    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": this.csrfToken
        },
        body: JSON.stringify(params)
      })
      const data = await response.json()

      // Update each triggered facet with new options
      for (const [key, options] of Object.entries(data)) {
        const target = this.facetTargets.find(el => el.dataset.facetKey === key)
        if (target) this.updateOptions(target, options)
      }
    } catch (e) {
      // Silently fail -- form still works via manual submit
    }

    this.updateSubmitState()
  }

  // Collect current values from all facet targets (and, in refresh-all mode,
  // the plain input targets such as dates and radios)
  currentValues() {
    const values = {}
    this.facetTargets.forEach(el => {
      const key = el.dataset.facetKey
      if (el.multiple) {
        const selected = Array.from(el.selectedOptions).map(o => o.value).filter(v => v)
        if (selected.length > 0) values[key] = selected
      } else {
        if (el.value) values[key] = el.value
      }
    })
    this.inputTargets.forEach(el => {
      if ((el.type === "radio" || el.type === "checkbox") && !el.checked) return
      if (el.value) values[el.dataset.facetKey] = el.value
    })
    return values
  }

  // Replace options on a <select> without destroying Slim Select, keeping
  // any still-present selection (lib/slim_options.js, shared with the
  // faceted-filter family per ADR-0059)
  updateOptions(selectEl, options) {
    const currentValues = new Set(
      Array.from(selectEl.selectedOptions).map(o => o.value)
    )

    swapSelectOptions(this.application, selectEl, this.optionItems(options, currentValues))
  }

  // [[label, value, { html }], ...] -> Slim/native option items
  optionItems(options, selectedValues) {
    return options.map(([label, value, extra]) => {
      const item = { text: label, value: String(value), selected: selectedValues.has(String(value)) }
      if (extra?.html) item.html = extra.html
      return item
    })
  }

  // --- Refresh-all mode ---------------------------------------------------

  // Debounce: collect the keys that changed, fire one request once the
  // user pauses. A null key means "refresh every facet".
  queueRefresh(key) {
    if (key) {
      this._changedKeys.add(key)
    } else {
      this._refreshEverything = true
    }

    clearTimeout(this._debounceTimer)
    this._debounceTimer = setTimeout(() => this.refreshAll(), this.debounceValue)
  }

  async refreshAll() {
    // The changed facet can skip its own refresh (its options don't depend
    // on its own value) -- but only when it is the ONLY change since the
    // last completed refresh. Several changes, a forced refresh, or an
    // aborted request mean every facet refreshes.
    const inFlight = !!this._abortController
    const changed = !inFlight && !this._refreshEverything && this._changedKeys.size === 1
      ? [...this._changedKeys][0]
      : null
    this._changedKeys = new Set()
    this._refreshEverything = false

    this._abortController?.abort()
    const abortController = new AbortController()
    this._abortController = abortController

    const params = this.currentValues()
    params.facets = "all"
    if (changed) params.changed = changed

    const refreshing = this.facetTargets.filter(el => el.dataset.facetKey !== changed)
    this.setLoading(refreshing)

    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": this.csrfToken
        },
        body: JSON.stringify(params),
        signal: abortController.signal
      })
      if (!response.ok) throw new Error(`facet_options ${response.status}`)
      const data = await response.json()
      if (abortController.signal.aborted) return

      const dropped = this.applyFacets(data.facets || {})
      this.updateCount(data)
      this.updateScopeNotes(data.facets || {}, data.unscoped || [])
      this.showDropped(dropped)
      this.updateSubmitState()

      // Dropping a selection narrows the search -- refresh again off it.
      if (dropped.length > 0) this.queueRefresh(null)
    } catch (e) {
      // Aborted by a newer request, or a failed fetch: the form still works
      // via a manual submit, so fail quietly.
    } finally {
      if (this._abortController === abortController) {
        this._abortController = null
        this.setLoading([])
      }
    }
  }

  // Swap each returned facet's options; returns the selections dropped
  // because they're no longer among the options.
  applyFacets(facets) {
    const dropped = []

    Object.entries(facets).forEach(([key, options]) => {
      const el = this.facetTargets.find(target => target.dataset.facetKey === key)
      if (!el) return

      const valid = new Set(options.map(([, value]) => String(value)))
      const selected = Array.from(el.selectedOptions).filter(o => o.value)
      const kept = new Set()
      selected.forEach(option => {
        if (valid.has(option.value)) {
          kept.add(option.value)
        } else {
          dropped.push(`${el.dataset.facetLabel || key}: ${option.textContent.trim()}`)
        }
      })

      // Skip the rebuild when nothing about this select would change.
      const signature = JSON.stringify([options, [...kept]])
      if (this._optionSignatures.get(el) === signature && kept.size === selected.length) return
      this._optionSignatures.set(el, signature)

      swapSelectOptions(this.application, el, this.optionItems(options, kept))
    })

    return dropped
  }

  // Per-select loading state: dim and pulse each facet being refreshed.
  setLoading(elements) {
    const loading = new Set(elements)
    this.facetTargets.forEach(el => {
      const box = el.parentElement
      if (!box) return
      const isLoading = loading.has(el)
      box.classList.toggle("opacity-50", isLoading)
      box.classList.toggle("animate-pulse", isLoading)
      box.setAttribute("aria-busy", isLoading ? "true" : "false")
    })
  }

  updateCount(data) {
    if (!this.hasCountTarget || typeof data.count !== "number") return

    const n = data.count.toLocaleString("en-AU")
    let text
    if (data.count_capped) {
      text = `${n}+ images`
    } else if (data.count === 0) {
      text = "No images match"
    } else {
      text = `about ${n} ${data.count === 1 ? "image" : "images"}`
    }
    this.countTargets.forEach(el => { el.textContent = text })
  }

  // Flag facets showing their global list because the scope was too big to
  // narrow cheaply (server `unscoped`). Only facets in this response change.
  updateScopeNotes(facets, unscoped) {
    const unscopedKeys = new Set(unscoped)
    this.scopeNoteTargets.forEach(el => {
      const key = el.dataset.facetKey
      if (!(key in facets)) return
      el.classList.toggle("hidden", !unscopedKeys.has(key))
    })
  }

  showDropped(dropped) {
    if (!this.hasDroppedTarget || dropped.length === 0) return

    this.droppedTarget.textContent = `Cleared, no longer matching: ${dropped.join("; ")}`
    this.droppedTarget.classList.remove("hidden")
    clearTimeout(this._droppedTimeout)
    this._droppedTimeout = setTimeout(() => this.droppedTarget.classList.add("hidden"), 8000)
  }

  // Called on submit button click -- prevents submission when filters are missing
  submitClicked(e) {
    if (!this.hasSubmitTarget) return

    const missing = this.missingRequired()
    if (missing.length === 0) return // Allow normal submission

    e.preventDefault()
    if (this.hasHintTarget) {
      const labels = this.requiredLabelsValue
      const names = missing.map(key => labels[key] || key.replace(/_/g, " "))
      this.hintTarget.textContent = `Required: ${names.join(", ")}`
      this.hintTarget.classList.remove("hidden")
      clearTimeout(this._hintTimeout)
      this._hintTimeout = setTimeout(() => this.hintTarget.classList.add("hidden"), 4000)
    }
  }

  missingRequired() {
    const values = this.currentValues()
    return this.requiredValue.filter(key => {
      const val = values[key]
      return Array.isArray(val) ? val.length === 0 : !val
    })
  }

  // Style submit button based on whether all required facets have values
  updateSubmitState() {
    if (!this.hasSubmitTarget) return
    if (this.requiredValue.length === 0) return

    const missing = this.missingRequired()
    const allPresent = missing.length === 0

    if (allPresent) {
      this.submitTarget.classList.remove("opacity-50", "cursor-not-allowed")
      if (this.hasHintTarget) this.hintTarget.classList.add("hidden")
    } else {
      this.submitTarget.classList.add("opacity-50", "cursor-not-allowed")
    }

    // Reset spinner state when re-enabling
    if (this.hasSubmitLabelTarget) this.submitLabelTarget.classList.remove("hidden")
    if (this.hasSubmitSpinnerTarget) {
      this.submitSpinnerTarget.classList.add("hidden")
      this.submitSpinnerTarget.classList.remove("flex")
    }
  }

  get csrfToken() {
    return document.querySelector("meta[name='csrf-token']")?.content || ""
  }
}
